/**
 * Der **Wechselrichter als Gerät** (HM-126, ADR 0039): PV-Wechselrichter,
 * Hybrid-Wechselrichter und Batteriewechselrichter, gelesen von einem Gateway
 * — über SunSpec, über die Schnittstelle eines Herstellers, über eine DTU.
 *
 * **Ein Vertrag für alle drei, und die Art folgt aus dem, was gemeldet wird.**
 * Ein Hybrid-Wechselrichter ist ein Gerät mit einem PV-Eingang, einem
 * Batterieanschluss und einer Netzseite; ein PV-Wechselrichter hat keinen
 * Batterieanschluss, ein Batteriewechselrichter keinen PV-Eingang. Das sind die
 * **Kanäle** des Geräts (ADR 0020 §3: physische Anschlussgruppen) — und weil
 * ein Kanal ein physischer Zähler ist (HM-058), macht der Anlagevorschlag aus
 * jedem seinen eigenen: den PV-Ertrag, den Speicherzähler, die Netzseite. Ob
 * es ein Hybrid **ist**, sagt der Katalog und nicht eine Typangabe (Regel 9).
 *
 * **Der Batteriekanal spricht den Speichervertrag** (`storage.ts`): dieselben
 * vier Schlüssel, dieselbe Deutung (`storageStateOf`). Ein Speicher an einem
 * Hybrid ist kein anderer Speicher als einer hinter EVCC.
 *
 * **Die Vorzeichen folgen dem Netzzähler.** Eine Leistung mit der Rolle
 * `power_total` ist positiv in Bezugsrichtung ihres Kanals — so liest
 * `instantWatts` sie für die Momentanwerte. Am PV-Eingang, der nur erzeugt, ist
 * sie deshalb **negativ**, sobald Strom kommt, und sein Ertrag ist die
 * Einspeisung (`energy_export_total`): Daraus wird ein Zähler der Bauart
 * `generation`. Für Menschen dreht erst `inverterStateOf` das Vorzeichen um.
 *
 * **Und die Netzseite ist die Summe dessen, was dahinter steht.** Was am
 * AC-Ausgang eines Hybrids herauskommt, ist PV plus Entladung minus Ladung.
 * Wer im Messkonzept die Netzseite **und** einen der Teile steckt, zählt ihn
 * doppelt — `inverterDoubleCounts` findet das, die API lehnt es ab.
 *
 * Reine Regeln: ohne Datenbank, ohne Uhr, ohne I/O.
 */
import type { RegisterRole } from "../meter/meter-register.js";
import type { Unit } from "../common/units.js";
import type { DatapointAccess } from "./datapoint.js";
import {
  STORAGE_DATAPOINTS,
  storageStateOf,
  type StorageDatapointSource,
  type StorageField,
  type StorageState,
  type StorageValueSource,
} from "./storage.js";

/**
 * Die Gerätetypen, für die dieser Vertrag gilt. **Der Schlüssel nennt die
 * Quelle** (ADR 0032, Nachtrag): Aufgenommen sind die Wege, auf denen das
 * Gateway Wechselrichter heute schon **findet** — SunSpec als herstellerneutrale
 * Norm, die Schnittstellen von Fronius, Huawei, Sungrow und GoodWe, und die
 * beiden DTUs für Mikrowechselrichter. Eine weitere Quelle ist ein Eintrag
 * hier, keine neue Deutung.
 */
export const INVERTER_DEVICE_TYPES = [
  "sunspec_inverter",
  "fronius_inverter",
  "huawei_inverter",
  "sungrow_inverter",
  "goodwe_inverter",
  "opendtu_inverter",
  "ahoydtu_inverter",
  /**
   * Hinter einem **Victron GX** (HM-129): ein MPPT-Laderegler ist ein
   * Wechselrichter mit nur einem PV-Eingang, ein `pvinverter` ein
   * AC-gekoppelter PV-Wechselrichter, den das GX mitliest. Je Gerät am GX ein
   * Knoten, nicht einer je GX — zwei Laderegler an zwei Dachflächen sind zwei
   * Erzeugungsanlagen.
   */
  "victron_solarcharger",
  "victron_pvinverter",
] as const;
export type InverterDeviceType = (typeof INVERTER_DEVICE_TYPES)[number];

export function isInverterDeviceType(deviceType: string): boolean {
  return (INVERTER_DEVICE_TYPES as readonly string[]).includes(deviceType);
}

/** Die drei Anschlussgruppen — zugleich die Kanalschlüssel im Katalog. */
export const INVERTER_PARTS = ["pv", "battery", "ac"] as const;
export type InverterPart = (typeof INVERTER_PARTS)[number];

/** Der Name, den das Gateway dem Kanal gibt — er wird der Vorschlagsname des Zählers. */
export const INVERTER_CHANNEL_NAMES: Readonly<Record<InverterPart, string>> = {
  pv: "PV-Eingang",
  battery: "Batterie",
  ac: "Netzseite",
};

export interface InverterDatapointSpec {
  readonly key: string;
  readonly name: string;
  readonly channel: InverterPart;
  readonly unit: Unit | null;
  readonly access: DatapointAccess;
  readonly role: RegisterRole | null;
}

const battery = (
  spec: (typeof STORAGE_DATAPOINTS)[keyof typeof STORAGE_DATAPOINTS],
): InverterDatapointSpec => ({ ...spec, channel: "battery" });

export const INVERTER_DATAPOINTS = {
  /** Negativ, solange die Module liefern — Bezugsrichtung des Kanals (siehe oben). */
  pvPower: {
    key: "pv-power",
    name: "PV-Leistung (− Erzeugung)",
    channel: "pv",
    unit: "W",
    access: "read",
    role: "power_total",
  },
  pvEnergy: {
    key: "pv-energy",
    name: "PV-Ertrag gesamt",
    channel: "pv",
    unit: "Wh",
    access: "read",
    role: "energy_export_total",
  },
  batterySoc: battery(STORAGE_DATAPOINTS.soc),
  batteryPower: battery(STORAGE_DATAPOINTS.power),
  batteryEnergyCharged: battery(STORAGE_DATAPOINTS.energyCharged),
  batteryEnergyDischarged: battery(STORAGE_DATAPOINTS.energyDischarged),
  /** Positiv bezieht er aus dem Hausnetz, negativ gibt er ab — wie der Netzzähler. */
  acPower: {
    key: "ac-power",
    name: "Leistung Netzseite (+ Bezug, − Abgabe)",
    channel: "ac",
    unit: "W",
    access: "read",
    role: "power_total",
  },
  acEnergyImport: {
    key: "ac-energy-import",
    name: "Aus dem Hausnetz bezogen",
    channel: "ac",
    unit: "Wh",
    access: "read",
    role: "energy_import_total",
  },
  acEnergyExport: {
    key: "ac-energy-export",
    name: "Ins Hausnetz abgegeben",
    channel: "ac",
    unit: "Wh",
    access: "read",
    role: "energy_export_total",
  },
} as const satisfies Readonly<Record<string, InverterDatapointSpec>>;

export type InverterDatapointName = keyof typeof INVERTER_DATAPOINTS;

const PART_OF_KEY: ReadonlyMap<string, InverterPart> = new Map(
  Object.values(INVERTER_DATAPOINTS).map((spec) => [spec.key, spec.channel]),
);

/**
 * Zu welchem Teil ein Schlüssel gehört — `null` für jeden, den der Vertrag
 * nicht kennt. Gedeutet wird über den Schlüssel und nicht über den Kanal: Der
 * Schlüssel ist gerätelokal eindeutig, und der Vertrag legt fest, in welchem
 * Kanal er steht.
 */
export function inverterPartOfKey(key: string): InverterPart | null {
  return PART_OF_KEY.get(key) ?? null;
}

/** Was ein Wechselrichter ist, gemessen an dem, was er meldet. */
export type InverterKind = "pv" | "hybrid" | "battery";

export interface InverterPvState {
  /** Die gemeldete Leistung mit Vorzeichen — negativ bei Erzeugung. */
  readonly powerW: StorageField<number>;
  /** Für Menschen: was gerade von den Modulen kommt — nie negativ. */
  readonly generationW: number | null;
  readonly energyWh: StorageField<number>;
}

export interface InverterAcState {
  /** Die gemeldete Leistung mit Vorzeichen — positiv bezieht er. */
  readonly powerW: StorageField<number>;
  readonly energyImportWh: StorageField<number>;
  readonly energyExportWh: StorageField<number>;
}

export interface InverterState {
  /** `null`, solange weder PV-Eingang noch Batterie gemeldet sind. */
  readonly kind: InverterKind | null;
  /** `null`, wenn kein Schlüssel des PV-Eingangs im Katalog steht. */
  readonly pv: InverterPvState | null;
  /** Der Speichervertrag am Batteriekanal; `null` ohne Batterie. */
  readonly battery: Omit<StorageState, "measuredAt"> | null;
  readonly ac: InverterAcState | null;
  /** Der jüngste Messzeitpunkt über alle Felder — `null`, solange nichts kam. */
  readonly measuredAt: Date | null;
}

/**
 * Der Zustand eines Wechselrichters aus seinem Katalog und den zuletzt
 * gemeldeten Werten. Ein Teil, von dem kein Schlüssel im Katalog steht, ist
 * `null` — nicht ein Teil mit lauter leeren Feldern: „hat keine Batterie" und
 * „hat noch nichts gemeldet" sind zwei Aussagen.
 */
export function inverterStateOf(
  datapoints: readonly StorageDatapointSource[],
  values: readonly StorageValueSource[],
): InverterState {
  const byKey = new Map(
    datapoints.filter((point) => point.valueType === "number").map((point) => [point.key, point]),
  );
  const valueById = new Map(values.map((value) => [value.datapointId, value]));
  const has = (part: InverterPart): boolean =>
    [...byKey.keys()].some((key) => inverterPartOfKey(key) === part);

  let measuredAt = null as Date | null;
  const field = (name: InverterDatapointName): StorageField<number> => {
    const point = byKey.get(INVERTER_DATAPOINTS[name].key);
    if (point === undefined) return { value: null, datapointId: null };
    const sample = valueById.get(point.id);
    if (sample === undefined) return { value: null, datapointId: point.id };
    if (measuredAt === null || sample.measuredAt > measuredAt) measuredAt = sample.measuredAt;
    return { value: Number.isFinite(sample.value) ? sample.value : null, datapointId: point.id };
  };

  let pv: InverterPvState | null = null;
  if (has("pv")) {
    const powerW = field("pvPower");
    pv = {
      powerW,
      generationW: powerW.value === null ? null : Math.max(-powerW.value, 0),
      energyWh: field("pvEnergy"),
    };
  }

  let batteryState: InverterState["battery"] = null;
  if (has("battery")) {
    const { measuredAt: batteryAt, ...rest } = storageStateOf(datapoints, values);
    if (batteryAt !== null && (measuredAt === null || batteryAt > measuredAt)) {
      measuredAt = batteryAt;
    }
    batteryState = rest;
  }

  const ac: InverterAcState | null = has("ac")
    ? {
        powerW: field("acPower"),
        energyImportWh: field("acEnergyImport"),
        energyExportWh: field("acEnergyExport"),
      }
    : null;

  const kind: InverterKind | null =
    pv !== null && batteryState !== null
      ? "hybrid"
      : pv !== null
        ? "pv"
        : batteryState !== null
          ? "battery"
          : null;

  return { kind, pv, battery: batteryState, ac, measuredAt };
}

/** Eine Quelle, die ein Messkonzept über seine Messstellen liest. */
export interface InverterConceptSource {
  readonly deviceId: string;
  readonly deviceType: string;
  readonly datapointKey: string;
}

/** Ein Wechselrichter, dessen Energie das Konzept doppelt zählte. */
export interface InverterDoubleCount {
  readonly deviceId: string;
  /** Die Teile hinter der Netzseite, die zusätzlich gesteckt sind. */
  readonly parts: readonly Exclude<InverterPart, "ac">[];
}

/**
 * Findet Wechselrichter, deren **Netzseite und** einer der Teile dahinter im
 * selben Messkonzept stehen.
 *
 * Die Netzseite eines Hybrids zählt PV plus Entladung minus Ladung. Steckt sie
 * im Erzeugungszähler und die Batterie im Speicher, rechnet die Hausvorlage
 * `Erzeugung + Entladung − Ladung` — und die Batterie steht zweimal darin.
 * Dasselbe gilt für PV-Eingang und Netzseite eines reinen PV-Wechselrichters
 * im selben Konzept, auch als zwei Summenquellen eines virtuellen Zählers.
 * Entweder die Netzseite **oder** die Teile, nie beides.
 *
 * Nur für Geräte dieses Vertrags: Die Schlüssel eines fremden Geräts bedeuten
 * nichts davon.
 */
export function inverterDoubleCounts(
  sources: readonly InverterConceptSource[],
): readonly InverterDoubleCount[] {
  const partsByDevice = new Map<string, Set<InverterPart>>();
  for (const source of sources) {
    if (!isInverterDeviceType(source.deviceType)) continue;
    const part = inverterPartOfKey(source.datapointKey);
    if (part === null) continue;
    const parts = partsByDevice.get(source.deviceId) ?? new Set<InverterPart>();
    parts.add(part);
    partsByDevice.set(source.deviceId, parts);
  }

  const result: InverterDoubleCount[] = [];
  for (const [deviceId, parts] of partsByDevice) {
    if (!parts.has("ac")) continue;
    const behind = (["pv", "battery"] as const).filter((part) => parts.has(part));
    if (behind.length > 0) result.push({ deviceId, parts: behind });
  }
  return result;
}
